// TelcoTrack — agregované pokrytí spoluvlastníků (A33) + SJM podpisy (A34).

import { parseShareFraction, sumOwnerShareFractions } from './bremena-party-share.util';

export interface BremenaCoOwnerCoverageMember {
  subjectId: string;
  /** Podíl na LV (číselník); null = neznámý. */
  shareNumerator?: number | null;
  shareDenominator?: number | null;
  /** Strana má nástroj / party vazbu pokrývající skupinu. */
  covered: boolean;
  signatureRequired: boolean;
  signed: boolean;
}

export interface BremenaCoOwnerGroupCoverage {
  ownersCovered: number;
  ownersTotal: number;
  /** např. `3/4` */
  ownersLabel: string;
  /** Součet podílů pokrytých vlastníků (0–1); null když žádný podíl. */
  shareCoveredFraction: number | null;
  /** Zaokrouhlené % (75); null když žádný podíl. */
  shareCoveredPercent: number | null;
  signaturesPresent: number;
  signaturesRequired: number;
  /** např. `2/3` */
  signaturesLabel: string;
  /** false když chybí povinný podpis (A34 SJM). */
  signatureComplete: boolean;
}

function clampPercent(fraction: number): number {
  return Math.round(fraction * 1000) / 10; // one decimal if needed; usually whole %
}

/**
 * Agregované pokrytí skupiny spoluvlastníků / SJM.
 * Správce sem nepatří (filtruj před voláním). Délka VB se sem nepočítá — nesmí se násobit podíly.
 */
export function aggregateCoOwnerGroupCoverage(
  members: readonly BremenaCoOwnerCoverageMember[],
): BremenaCoOwnerGroupCoverage {
  const ownersTotal = members.length;
  const ownersCovered = members.filter((m) => m.covered).length;
  const coveredShares = members
    .filter((m) => m.covered)
    .map((m) => ({
      numerator: m.shareNumerator ?? null,
      denominator: m.shareDenominator ?? null,
    }));
  const shareCoveredFraction = sumOwnerShareFractions(coveredShares);
  const shareCoveredPercent =
    shareCoveredFraction == null ? null : Math.round(clampPercent(shareCoveredFraction));

  const required = members.filter((m) => m.signatureRequired);
  const signaturesRequired = required.length;
  const signaturesPresent = required.filter((m) => m.signed).length;

  return {
    ownersCovered,
    ownersTotal,
    ownersLabel: `${ownersCovered}/${ownersTotal}`,
    shareCoveredFraction,
    shareCoveredPercent,
    signaturesPresent,
    signaturesRequired,
    signaturesLabel: `${signaturesPresent}/${signaturesRequired}`,
    signatureComplete: signaturesRequired === 0 || signaturesPresent >= signaturesRequired,
  };
}

/** Parse `1/4` share label from UI/API. */
export function parseShareLabel(
  label: string | null | undefined,
): { numerator: number | null; denominator: number | null } {
  const text = (label ?? '').trim();
  if (!text) return { numerator: null, denominator: null };
  const match = /^(\d+)\s*\/\s*(\d+)$/.exec(text);
  if (!match) return { numerator: null, denominator: null };
  return { numerator: Number(match[1]), denominator: Number(match[2]) };
}

export { parseShareFraction, sumOwnerShareFractions };
